const JAVA_CLASS_MAGIC = 0xcafe_babe;

export interface ClassFieldMetadata {
  readonly name: string;
  readonly descriptor: string;
  readonly type: string;
  readonly accessFlags: number;
}

export interface ClassMethodMetadata {
  readonly name: string;
  readonly descriptor: string;
  readonly parameterTypes: readonly string[];
  readonly returnType: string;
  readonly accessFlags: number;
}

export interface InnerClassMetadata {
  readonly internalName: string;
  readonly outerInternalName: string | null;
  readonly simpleName: string | null;
  readonly accessFlags: number;
}

export type ClassKind =
  | "class"
  | "interface"
  | "enum"
  | "annotation"
  | "module";

export interface ClassFileMetadata {
  readonly internalName: string;
  readonly simpleName: string;
  readonly kind: ClassKind;
  readonly accessFlags: number;
  readonly majorVersion: number;
  readonly minorVersion: number;
  readonly fields: readonly ClassFieldMetadata[];
  readonly methods: readonly ClassMethodMetadata[];
  readonly innerClasses: readonly InnerClassMetadata[];
  readonly enclosingClassInternalName: string | null;
}

class ClassReader {
  private readonly data: Uint8Array;
  private readonly view: DataView;
  private offset = 0;

  public constructor(data: Uint8Array) {
    this.data = data;
    this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  }

  public readU1(): number {
    this.ensure(1);
    return this.data[this.offset++]!;
  }

  public readU2(): number {
    this.ensure(2);
    const value = this.view.getUint16(this.offset, false);
    this.offset += 2;
    return value;
  }

  public readU4(): number {
    this.ensure(4);
    const value = this.view.getUint32(this.offset, false);
    this.offset += 4;
    return value;
  }

  public readBytes(length: number): Uint8Array {
    this.ensure(length);
    const bytes = this.data.subarray(this.offset, this.offset + length);
    this.offset += length;
    return bytes;
  }

  public skip(length: number): void {
    this.ensure(length);
    this.offset += length;
  }

  private ensure(length: number): void {
    if (length < 0 || this.offset + length > this.data.length) {
      throw new Error("Unexpected end of class file");
    }
  }
}

interface ConstantPool {
  readonly utf8: readonly (string | undefined)[];
  readonly classNameIndices: readonly (number | undefined)[];
}

function getUtf8(pool: ConstantPool, index: number): string {
  const value = pool.utf8[index];
  if (value === undefined) {
    throw new Error(`Invalid CONSTANT_Utf8 index ${index}`);
  }
  return value;
}

function getClassName(pool: ConstantPool, index: number): string {
  const nameIndex = pool.classNameIndices[index];
  if (nameIndex === undefined) {
    throw new Error(`Invalid CONSTANT_Class index ${index}`);
  }
  return getUtf8(pool, nameIndex);
}

function parseConstantPool(reader: ClassReader): ConstantPool {
  const count = reader.readU2();
  const utf8: (string | undefined)[] = new Array(count);
  const classNameIndices: (number | undefined)[] = new Array(count);
  const decoder = new TextDecoder("utf-8", { fatal: false });

  for (let index = 1; index < count; index++) {
    const tag = reader.readU1();

    switch (tag) {
      case 1: {
        const length = reader.readU2();
        utf8[index] = decoder.decode(reader.readBytes(length));
        break;
      }
      case 3:
      case 4:
        reader.skip(4);
        break;
      case 5:
      case 6:
        reader.skip(8);
        index++;
        break;
      case 7:
        classNameIndices[index] = reader.readU2();
        break;
      case 8:
      case 16:
      case 19:
      case 20:
        reader.skip(2);
        break;
      case 9:
      case 10:
      case 11:
      case 12:
      case 17:
      case 18:
        reader.skip(4);
        break;
      case 15:
        reader.skip(3);
        break;
      default:
        throw new Error(`Unsupported class-file constant-pool tag ${tag}`);
    }
  }

  return { utf8, classNameIndices };
}

function skipAttributes(reader: ClassReader): void {
  const attributeCount = reader.readU2();
  for (let index = 0; index < attributeCount; index++) {
    reader.skip(2);
    const length = reader.readU4();
    reader.skip(length);
  }
}

function simpleTypeName(internalName: string): string {
  const slash = internalName.lastIndexOf("/");
  return internalName.slice(slash + 1).replaceAll("$", ".");
}

function parseDescriptorType(
  descriptor: string,
  initialOffset = 0,
  allowVoid = false,
): { readonly type: string; readonly nextOffset: number } {
  let offset = initialOffset;
  let dimensions = 0;

  while (descriptor[offset] === "[") {
    dimensions++;
    offset++;
  }

  const marker = descriptor[offset];
  let type: string;
  let nextOffset: number;

  switch (marker) {
    case "B":
      type = "byte";
      nextOffset = offset + 1;
      break;
    case "C":
      type = "char";
      nextOffset = offset + 1;
      break;
    case "D":
      type = "double";
      nextOffset = offset + 1;
      break;
    case "F":
      type = "float";
      nextOffset = offset + 1;
      break;
    case "I":
      type = "int";
      nextOffset = offset + 1;
      break;
    case "J":
      type = "long";
      nextOffset = offset + 1;
      break;
    case "S":
      type = "short";
      nextOffset = offset + 1;
      break;
    case "Z":
      type = "boolean";
      nextOffset = offset + 1;
      break;
    case "V":
      if (!allowVoid || dimensions !== 0) {
        throw new Error(`Invalid descriptor: ${descriptor}`);
      }
      type = "void";
      nextOffset = offset + 1;
      break;
    case "L": {
      const end = descriptor.indexOf(";", offset);
      if (end === -1) {
        throw new Error(`Invalid descriptor: ${descriptor}`);
      }
      type = simpleTypeName(descriptor.slice(offset + 1, end));
      nextOffset = end + 1;
      break;
    }
    default:
      throw new Error(`Invalid descriptor: ${descriptor}`);
  }

  return {
    type: `${type}${"[]".repeat(dimensions)}`,
    nextOffset,
  };
}

export function descriptorToType(descriptor: string): string {
  const result = parseDescriptorType(descriptor);
  if (result.nextOffset !== descriptor.length) {
    throw new Error(`Invalid field descriptor: ${descriptor}`);
  }
  return result.type;
}

export function methodDescriptorToTypes(descriptor: string): {
  readonly parameterTypes: readonly string[];
  readonly returnType: string;
} {
  if (descriptor[0] !== "(") {
    throw new Error(`Invalid method descriptor: ${descriptor}`);
  }

  const parameterTypes: string[] = [];
  let offset = 1;
  while (descriptor[offset] !== ")") {
    if (offset >= descriptor.length) {
      throw new Error(`Invalid method descriptor: ${descriptor}`);
    }
    const parameter = parseDescriptorType(descriptor, offset);
    parameterTypes.push(parameter.type);
    offset = parameter.nextOffset;
  }

  const returned = parseDescriptorType(descriptor, offset + 1, true);
  if (returned.nextOffset !== descriptor.length) {
    throw new Error(`Invalid method descriptor: ${descriptor}`);
  }

  return {
    parameterTypes,
    returnType: returned.type,
  };
}

function classKind(accessFlags: number): ClassKind {
  if ((accessFlags & 0x8000) !== 0) {
    return "module";
  }
  if ((accessFlags & 0x2000) !== 0) {
    return "annotation";
  }
  if ((accessFlags & 0x4000) !== 0) {
    return "enum";
  }
  if ((accessFlags & 0x0200) !== 0) {
    return "interface";
  }
  return "class";
}

function parseClassAttributes(
  reader: ClassReader,
  pool: ConstantPool,
): {
  readonly innerClasses: readonly InnerClassMetadata[];
  readonly enclosingClassInternalName: string | null;
} {
  const innerClasses: InnerClassMetadata[] = [];
  let enclosingClassInternalName: string | null = null;
  const attributeCount = reader.readU2();

  for (let index = 0; index < attributeCount; index++) {
    const name = getUtf8(pool, reader.readU2());
    const data = new ClassReader(reader.readBytes(reader.readU4()));

    if (name === "InnerClasses") {
      const classCount = data.readU2();
      for (let classIndex = 0; classIndex < classCount; classIndex++) {
        const innerClassIndex = data.readU2();
        const outerClassIndex = data.readU2();
        const innerNameIndex = data.readU2();
        const accessFlags = data.readU2();

        if (innerClassIndex === 0) {
          continue;
        }

        innerClasses.push({
          internalName: getClassName(pool, innerClassIndex),
          outerInternalName:
            outerClassIndex === 0 ? null : getClassName(pool, outerClassIndex),
          simpleName: innerNameIndex === 0 ? null : getUtf8(pool, innerNameIndex),
          accessFlags,
        });
      }
    } else if (name === "EnclosingMethod") {
      const classIndex = data.readU2();
      data.readU2();
      enclosingClassInternalName = getClassName(pool, classIndex);
    }
  }

  return { innerClasses, enclosingClassInternalName };
}

export function parseClassFileMetadata(data: Uint8Array): ClassFileMetadata {
  const reader = new ClassReader(data);

  if (reader.readU4() !== JAVA_CLASS_MAGIC) {
    throw new Error("Invalid CLASS file magic number");
  }

  const minorVersion = reader.readU2();
  const majorVersion = reader.readU2();
  const pool = parseConstantPool(reader);
  const accessFlags = reader.readU2();
  const thisClassIndex = reader.readU2();
  reader.skip(2);

  const interfaceCount = reader.readU2();
  reader.skip(interfaceCount * 2);

  const fieldCount = reader.readU2();
  const fields: ClassFieldMetadata[] = [];

  for (let index = 0; index < fieldCount; index++) {
    const fieldAccessFlags = reader.readU2();
    const name = getUtf8(pool, reader.readU2());
    const descriptor = getUtf8(pool, reader.readU2());
    fields.push({
      name,
      descriptor,
      type: descriptorToType(descriptor),
      accessFlags: fieldAccessFlags,
    });
    skipAttributes(reader);
  }

  const methodCount = reader.readU2();
  const methods: ClassMethodMetadata[] = [];
  for (let index = 0; index < methodCount; index++) {
    const methodAccessFlags = reader.readU2();
    const name = getUtf8(pool, reader.readU2());
    const descriptor = getUtf8(pool, reader.readU2());
    const signature = methodDescriptorToTypes(descriptor);
    methods.push({
      name,
      descriptor,
      parameterTypes: signature.parameterTypes,
      returnType: signature.returnType,
      accessFlags: methodAccessFlags,
    });
    skipAttributes(reader);
  }

  const classAttributes = parseClassAttributes(reader, pool);
  const internalName = getClassName(pool, thisClassIndex);
  return {
    internalName,
    simpleName: simpleTypeName(internalName),
    kind: classKind(accessFlags),
    accessFlags,
    majorVersion,
    minorVersion,
    fields,
    methods,
    innerClasses: classAttributes.innerClasses,
    enclosingClassInternalName: classAttributes.enclosingClassInternalName,
  };
}
